import {Component, Inject, Input, OnInit} from '@angular/core';
import {NavigationExtras, Router} from '@angular/router';
import {CtaType, pageTypes, MyPlan, PlanCard, BML as iBML, UIFeatureFlag} from '@ox/typings';
import {SessionStorageService, WINDOW, buildTrackingString} from '@ox/utils';
import {Store} from '@ngrx/store';
import {selectActiveVehicle, selectFeatureFlag} from '@ox/data';
import {AutoDestroy, BML, AppConfigService} from '@ox/utils';
import {Subject, take, takeUntil} from 'rxjs';
import {ShopDataService} from '@webu/data';
import {CheckoutNavigationService} from '../../../services';

@Component({
  selector: 'webu-plan-card',
  templateUrl: './plan-card.component.html'
})
export class PlanCardComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  
  @Input() planCard: PlanCard;
  @Input() type: string;
  @Input() orderIdMap: {[key: string]: string};
  trackingPlanName: string;
  nonTransitionPlans: MyPlan[] = [];
  groupedTransitionPlans: MyPlan[][] = [];
  brand = this.bml.brand;
  canManagePlans = false;
  canSeePayments = false;
  managePlansLinkTxt = '';
  isNewCheckoutEnabled = false;
  isCancelPlanEnabled = false;
  isLoading = false;

  activeVehicle$ = this.store.select(selectActiveVehicle);
  
  checkoutToggle$ = this.store
  .select(
    selectFeatureFlag({id: UIFeatureFlag.CHECKOUT, group: 'UI'})
  )
  .pipe(take(1))
  .subscribe((response) => {
    if (response) {
      this.isNewCheckoutEnabled = response === 'true';
    }
  });

  cancelPlanToggle$ = this.store
    .select(selectFeatureFlag({id: UIFeatureFlag.CANCELLATION, group: 'UI'}))
    .pipe(take(1))
    .subscribe((flag) => {
      this.isCancelPlanEnabled = flag === 'true';
    });

  trackingString = (cta: CtaType, args: string[]) => buildTrackingString(pageTypes.myPlans, cta, args);

  constructor(
    private router: Router,
    @Inject(BML) private bml: iBML,
    @Inject(WINDOW) private window: Window,
    private store: Store,
    private appConfig: AppConfigService,
    private session : SessionStorageService,
    private shopDataService: ShopDataService,
    private checkoutNavigation: CheckoutNavigationService
  ) {}

  ngOnInit() {
    this.activeVehicle$.pipe(takeUntil(this.destroy$))
      .subscribe(vehicle => {
        if(vehicle?.roleCode === 'OWNER') this.canSeePayments = true;
        if(vehicle?.roleCode === 'OWNER' && this.appConfig.appSettings.moduleConfig.onstar.managePlans) {
          this.canManagePlans = true;
          
        }
      }
    );
    this.extractTransitionPlans();
    this.trackingPlanName = this.determinePlanNameForTracking();
    this.setCopy();
  }
  
  pushToGroupedTransitionPlans(tempGroup: MyPlan[]) {
    this.groupedTransitionPlans.push([...tempGroup]);
    tempGroup.length = 0;
  }
  
  extractTransitionPlans() {
    // check for next plan to group transition plans
    const tempGroup: MyPlan[] = [];
    this.planCard.plans.forEach((plan: MyPlan) => {
      if (plan.transitionTo) {
        const nextPlan = this.planCard.plans.find((nextPlan) => nextPlan.templateType === 'TRANSITION' && nextPlan.planCode === plan.transitionTo);
        tempGroup.push(plan);
        
        if (nextPlan) {
          tempGroup.push(nextPlan);
        }

        this.pushToGroupedTransitionPlans(tempGroup);

      } else if (plan.templateType !== 'TRANSITION') {
        this.nonTransitionPlans.push(plan);
      }
    });
  }

  handleClick(ctaAction: string, url?: string, planCode?: string) {
    ctaAction = ctaAction.toLowerCase();
    const actions : { [key: string]: () => void } = {
      'renew': ()  => this.goToRenew(url),
      'shop_plans': () => this.goToDlp(),
      'cancel': () => this.goToCancellation(planCode ?? ''),
      'checkout': () => this.goToCheckout(),
      'default': () => url && url !== '' && this.goToPdp(url)
    };

    (actions[ctaAction] || actions['default'])();
  }
  goToPdp(url: string) {
    this.router.navigate([`./onstar/shop/${url}`]);
  }

  goToDlp() {
    this.router.navigate(['./onstar/shop']);
  }

  goToCheckout() {
    this.isLoading = true;
    this.checkoutNavigation.goToCheckout([this.planCard.plans[0].planCode], this.router.url, '', undefined, 'PREPAID')
      .pipe(take(1))
      .subscribe({
        error: (err) => {
          this.isLoading = false;
          throw new Error(err);
        }
    });
  }

  goToRenew(url?: string) {
    this.session.setAsJson('plan', this.planCard.plans[0]);
    const oldPlan = this.planCard?.plans[0]?.planCode;
    const newPlan = url;
    this.window.location.href = `../onstar/shop/${url}?oldPlan=${oldPlan}&newPlan=${newPlan}`;
  }

  goToManagePlans() {
    this.router.navigate(['./onstar/myplans/plans-modify']);
  }

  /**
   * This function redirects user to my plan cancellation page.
   */
  goToCancellation(planCode: string) {
    const cancelOrderId = this.orderIdMap[planCode];
    this.router.navigate(['../onstar/cancellation/review'], {
      queryParams: {
        planCodes: planCode
      },
      cancelOrderId
    } as NavigationExtras
  );
  }

  determinePlanNameForTracking(){
    if(this.groupedTransitionPlans.length > 0) return this.groupedTransitionPlans[0][0].planName;
    else if(this.nonTransitionPlans.length > 0) return this.nonTransitionPlans[0].planName;
    else return '';
  }

  setCopy() {
    this.managePlansLinkTxt = this.shopDataService.getGenericCopy('myplans.services') ?? '';
  }
}
